<template>
  <banners>
    <div class="container-max mx-auto w-full px-4 py-4 text-left md:px-10">
      <h1 class="header-text text-left">Dispatchers</h1>
      <p class="mb-8 text-body-color">
        Dispatch Service companies can be authorized to onboard and represent
        your company. Pending Connections are requests from Dispatch Service
        companies to join your account. Keep your account secure by only
        approving requests from dispatchers you trust.
      </p>
      <!-- Pending Connections -->
      <div v-if="pendingConnections.length > 0">
        <p class="mb-6 text-1lg italic text-dark-blue">Pending Connections</p>
        <ul class="mb-8">
          <li
            v-for="connection in pendingConnections"
            :key="connection?.id"
          >
            <div
              class="mb-4 w-full rounded-md border-2 border-light-gray p-1md shadow-primary sm:relative"
              :class="{
                'flex w-full items-end justify-between':
                  screenSize !== 'mobile',
              }"
            >
              <div class="mb-4 sm:mb-0">
                <p class="mb-2 text-1lg text-dark-blue">
                  {{ connection?.dispatcher?.legal_name }}
                </p>
                <p
                  class="mb-4 text-xs text-body-color sm:absolute sm:right-4 sm:top-4"
                >
                  Requested at {{ formatRequestedDate(connection?.created_at) }}
                </p>
                <p class="my-3 text-xs font-bold uppercase text-dark-blue">
                  Requested By
                </p>
                <p class="mb-1 text-1md text-body-color">
                  {{ connection?.creator?.first_name }}
                  {{ connection?.creator?.last_name }}
                </p>
                <p class="mb-1 text-body-color">
                  {{ connection?.creator?.email }}
                </p>
                <p class="mb-1 text-body-color sm:mb-0">
                  {{
                    formatPhoneMethod(
                      connection?.creator?.phone_e164?.slice(-10)
                    )
                  }}
                </p>
              </div>
              <div
                class="flex"
                :class="[
                  { 'flex flex-col': screenSize === 'mobile' },
                  { 'flex-row-reverse gap-2': screenSize !== 'mobile' },
                ]"
              >
                <button-main
                  :size="screenSize === 'mobile' ? 'md' : '2md'"
                  :class="screenSize === 'mobile' ? 'mb-3' : ''"
                  @click="() => activateDispatcherConnection(connection?.id)"
                >
                  Accept
                </button-main>
                <button-main
                  variant="secondary"
                  :size="screenSize === 'mobile' ? 'md' : '2md'"
                  @click="() => onOpenDeclineModal(connection?.id)"
                >
                  Decline
                </button-main>
              </div>
            </div>
          </li>
        </ul>
      </div>

      <!-- Active Connections  -->
      <p class="mb-4 text-1lg text-dark-blue">Active Connections</p>
      <div v-if="activeConnections.length > 0">
        <ul>
          <li
            v-for="connection in activeConnections"
            :key="connection?.id"
            class="m-auto mb-4 flex items-center justify-between rounded-md border-2 border-light-gray p-1md shadow-primary"
          >
            <div class="flex flex-col">
              <div class="mb-1 text-dark-gray sm:mb-2">
                {{ connection?.dispatcher?.legal_name }}
              </div>
              <p class="text-1md text-body-color">
                Connected {{ formatDateOnly(connection?.activated_at) }}
              </p>
            </div>
            <img
              src="@/assets/trash-icon-2.svg"
              class="inline cursor-pointer"
              alt="trash icon"
              @click="deleteConnection(connection?.id)"
            />
          </li>
        </ul>
      </div>
      <div
        v-else
        class="mt-6 rounded-md bg-off-white px-6 py-8"
      >
        <p class="notice-text">
          You currently do not have any Dispatchers connected to this account.
        </p>
      </div>
    </div>
    <dispatch-request-modal
      v-if="declineModalVisible"
      :visible="declineModalVisible"
      type="decline"
      @close="onCloseDeclineModal"
      @submit="inactivateDispatcherConnection(id)"
    />
  </banners>
</template>
<script type="text/javascript">
import moment from 'moment';
import Banners from '@/components/Banners.vue';
import ButtonMain from '@/components/Button.vue';
import { formatPhone } from '@/common/validators';
import DispatchRequestModal from '@/components/modals/DispatchRequestModal.vue';

export default {
  name: 'Dispatcher Connections Index',
  components: {
    Banners,
    ButtonMain,
    DispatchRequestModal,
  },
  data() {
    return {
      declineModalVisible: false,
      recordId: 0,
      screenSize:
        window.innerWidth >= 640
          ? window.innerWidth <= 1024
            ? 'tablet'
            : 'desktop'
          : 'mobile',
    };
  },
  computed: {
    pendingConnections() {
      return this.$store.state.dispatcherConnections.collection.filter(
        (item) => item.status === 'pending'
      );
    },
    activeConnections() {
      return this.$store.state.dispatcherConnections.collection.filter(
        (item) => item.status === 'active'
      );
    },
  },
  async created() {
    await this.$store.dispatch(
      'dispatcherConnections/getDispatcherConnections'
    );

    addEventListener('resize', () => {
      switch (true) {
        case window.innerWidth <= 640:
          return (this.screenSize = 'mobile');
        case window.innerWidth <= 1024:
          return (this.screenSize = 'tablet');
        default:
          return (this.screenSize = 'desktop');
      }
    });
  },
  methods: {
    formatRequestedDate(date) {
      const requestedAt = moment(date).format('h:mm A  M/D/YY');
      return requestedAt;
    },
    formatDateOnly(date) {
      return moment(date).format('M/D/YY');
    },
    formatPhoneMethod(phone) {
      if (phone) {
        return formatPhone(phone);
      } else {
        return null;
      }
    },
    async activateDispatcherConnection(id) {
      let res = await this.$store.dispatch(
        'dispatcherConnections/activateDispatcherConnection',
        id
      );
      if ([200, 201].includes(res.status)) {
        this.$toast.open({
          message: 'Connection accepted',
          type: 'success',
          duration: 5000,
          dismissible: true,
          position: 'top-right',
        });
      }
    },
    async inactivateDispatcherConnection() {
      await this.$store.dispatch(
        'dispatcherConnections/inactivateDispatcherConnection',
        this.recordId
      );
      this.$toast.open({
        message: 'Dispatcher connection removed',
        type: 'success',
        duration: 5000,
        dismissible: true,
        position: 'top-right',
      });
      this.onCloseDeclineModal();
    },
    async deleteConnection(id) {
      this.recordId = id;
      this.inactivateDispatcherConnection();
    },
    onOpenDeclineModal(id) {
      this.recordId = id;
      this.declineModalVisible = true;
    },
    onCloseDeclineModal() {
      this.declineModalVisible = false;
    },
  },
};
</script>
<style scoped>
.header-text {
  font-size: 26px;
  @apply mb-4 text-dark-blue md:mb-8;
}

.notice-text {
  @apply text-center text-gray-over-dark;
  font-size: 16px;
}
</style>
